import React, { useState, useCallback, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Slider,
  Typography,
  Box,
  CircularProgress,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import Compressor from "compressorjs";
import DeleteIcon from "@material-ui/icons/Delete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  cropContainer: {
    position: "relative",
    width: "100%",
    height: 300,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  controls: {
    padding: theme.spacing(2, 0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  sliderContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  slider: {
    flex: 1,
  },
  loadingSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    minHeight: 200,
  },
  dialogActions: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rightActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  actionButton: {
    height: "32.5px",
    padding: "6px 16px",
  },
}));

// Helper to create cropped image
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.9
    );
  });
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const buildAbsoluteUrl = (baseUrl, relativePath) => {
  if (!baseUrl) return relativePath;

  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = relativePath.startsWith("/")
    ? relativePath
    : `/${relativePath}`;

  return `${normalizedBase}${normalizedPath}`;
};

const normalizeSrc = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value !== "string") return "";

  const trimmed = value.trim();
  if (!trimmed) return "";

  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "undefined" || lower === "-" || lower === "none") {
    return "";
  }

  if (
    lower.startsWith("http://") ||
    lower.startsWith("https://") ||
    lower.startsWith("data:") ||
    lower.startsWith("blob:")
  ) {
    return trimmed;
  }

  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }

  return buildAbsoluteUrl(process.env.REACT_APP_BACKEND_URL, `/public/avatar/${trimmed}`);
};

const AvatarUploadModal = ({ open, onClose, userId, onSuccess, onDelete, currentImage }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load current avatar to allow re-cropping without re-upload
  useEffect(() => {
    if (open && !imageSrc && currentImage) {
      const normalized = normalizeSrc(currentImage);
      if (normalized) {
        setImageSrc(normalized);
      }
    }
  }, [open, currentImage, imageSrc]);

  // Auto-trigger file input when modal opens (only if no image is set)
  useEffect(() => {
    if (open && !imageSrc && !currentImage) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, imageSrc, currentImage]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = async (e) => {
    // User cancelled file picker - close modal
    if (!e.target.files || !e.target.files[0]) {
      handleClose();
      return;
    }

    const file = e.target.files[0];

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(i18n.t("avatarUpload.errors.fileTooLarge"));
      return;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error(i18n.t("avatarUpload.errors.invalidType"));
      return;
    }

    // Compress image before showing in cropper
    new Compressor(file, {
      quality: 0.8,
      maxWidth: 1024,
      maxHeight: 1024,
      success(compressedFile) {
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          setImageSrc(reader.result);
        });
        reader.readAsDataURL(compressedFile);
      },
      error(err) {
        console.error(err);
        // Fallback to original file
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          setImageSrc(reader.result);
        });
        reader.readAsDataURL(file);
      },
    });

    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setLoading(true);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      const formData = new FormData();
      formData.append("avatar", croppedBlob, "avatar.jpg");

      const { data } = await api.post(`/users/${userId}/avatar`, formData);

      toast.success(i18n.t("avatarUpload.success"));
      onSuccess && onSuccess(data.profileImage);
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  };

  const handleDeleteAvatar = async () => {
    if (!userId) return;

    setLoading(true);

    try {
      await api.delete(`/users/${userId}/avatar`);
      toast.success(i18n.t("avatarUpload.deleteSuccess"));
      if (onDelete) {
        onDelete();
      }
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("avatarUpload.title")}</DialogTitle>
      <DialogContent dividers>
        {/* Hidden file input - auto-triggered when modal opens */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {!imageSrc ? (
          <Box className={classes.loadingSection}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="textSecondary">
              {i18n.t("avatarUpload.selectImage")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.cropContainer}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Box>
            <Box className={classes.controls}>
              <Box className={classes.sliderContainer}>
                <Typography variant="body2">Zoom</Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, zoom) => setZoom(zoom)}
                  className={classes.slider}
                />
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  setImageSrc(null);
                  // Trigger file picker again
                  setTimeout(() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }, 100);
                }}
              >
                {i18n.t("avatarUpload.changeImage")}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Box>
          {currentImage && (
            <Button
              onClick={handleDeleteAvatar}
              color="secondary"
              variant="outlined"
              startIcon={<DeleteIcon />}
              className={classes.actionButton}
              disabled={loading}
            >
              {i18n.t("avatarUpload.deleteAvatar")}
            </Button>
          )}
        </Box>
        <Box className={classes.rightActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="outlined"
            className={classes.actionButton}
            disabled={loading}
          >
            {i18n.t("avatarUpload.cancel")}
          </Button>
          <ButtonWithSpinner
            loading={loading}
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={loading || !imageSrc}
            type="button"
            className={classes.actionButton}
          >
            {i18n.t("avatarUpload.save")}
          </ButtonWithSpinner>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarUploadModal;
